import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';

interface EmptyStateProps {
  icon?: LucideIcon;
  /** Cor do ícone, em token. Ver a nota igual em `ModuleHeader`. */
  iconColor?: string;
  title: string;
  description?: string;
  actionLabel?: string;
  actionIcon?: LucideIcon;
  onAction?: () => void;
  /**
   * Versão reduzida, para superfície pequena: popover, painel estreito, coluna.
   *
   * Existe porque o mesmo componente pode ser MÓDULO num lugar e POPOVER em
   * outro. As notificações são o caso: viraram destino da barra do celular, onde
   * o vazio é de módulo e pede o estado cheio, mas no desktop continuam num
   * popover de 320px, onde ícone de 48px mais título de 18px ocupam mais altura
   * que a lista que deveriam estar substituindo.
   *
   * O caminho alternativo, deixar cada superfície pequena inventar o próprio
   * bloco de vazio, é como nasceram os parágrafos cinza em itálico espalhados
   * pelo produto: mesmo vazio, cinco desenhos. A variação mora no primitivo
   * justamente para não haver um segundo lugar onde consertar.
   */
  compact?: boolean;
  className?: string;
  [key: string]: unknown;
}

const EmptyState = ({
  icon: Icon,
  iconColor,
  title,
  description,
  actionLabel,
  actionIcon: ActionIcon,
  onAction,
  compact = false,
  className = '',
  ...props
}: EmptyStateProps) => {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        // No modo cheio a altura vem do pai (`h-full`); no compacto ela vem do
        // próprio respiro, porque popover não tem altura para herdar.
        compact ? 'px-6 py-8' : 'h-full p-10',
        className
      )}
      {...props}
    >
      {Icon && (
        <Icon
          size={compact ? 24 : 48}
          className={cn('text-[var(--color-text-muted)] opacity-50', compact ? 'mb-2' : 'mb-4')}
          style={iconColor ? { color: iconColor, opacity: 1 } : undefined}
        />
      )}
      <h3
        className={cn(
          'font-semibold text-[var(--color-text)] m-0',
          compact ? 'text-[13px] mb-1' : 'text-lg mb-2'
        )}
      >
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            'text-[var(--color-text-secondary)] m-0',
            compact ? 'text-[11px] mb-4' : 'mb-6'
          )}
        >
          {description}
        </p>
      )}
      {actionLabel && onAction && (
        <Button variant="primary" size="md" onClick={onAction}>
          {ActionIcon && <ActionIcon size={18} />}
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
